/**
 * Status, filter, and display config for subjects.
 */
import { BookOpen, GraduationCap, School, CheckCircle2, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';

/** Badge + icon config for a subject cycle. */
export interface SubjectCycleConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor: string;
  colorName: 'color-1' | 'color-2' | 'color-3';
}

/** Badge + icon config for a subject status. */
export interface SubjectStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Legacy / alternate spellings not used as API keys in {@link SUBJECT_CYCLE_CONFIG}. */
const EDUCATION_CYCLE_LABEL_FR_ALIASES: Record<string, string> = {
  primaire: 'Primaire',
  college: 'Collège',
  lycee: 'Lycée',
};

/** Returns the French label for an education cycle, including legacy aliases. */
export function getEducationCycleLabelFr(cycle: string | null | undefined): string {
  if (!cycle) return '';
  const fromConfig = SUBJECT_CYCLE_CONFIG[cycle]?.label;
  if (fromConfig) return fromConfig;
  const key = cycle.trim().toLowerCase();
  return EDUCATION_CYCLE_LABEL_FR_ALIASES[key] ?? cycle;
}

/** Badge config for each subject cycle. */
export const SUBJECT_CYCLE_CONFIG: Record<string, SubjectCycleConfig> = {
  primary: {
    label: 'Primaire',
    icon: School,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
    colorName: 'color-2',
  },
  middle: {
    label: 'Collège',
    icon: BookOpen,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-1)',
    colorName: 'color-1',
  },
  high: {
    label: 'Lycée',
    icon: GraduationCap,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-3-nuance-1)',
    colorName: 'color-3',
  },
};

/** Badge config for each subject status. */
export const SUBJECT_STATUS_CONFIG: Record<string, SubjectStatusConfig> = {
  active: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
  },
  inactive: {
    label: 'Inactif',
    icon: XCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** Subject list filter state. */
export interface SubjectFilters {
  cycles: string[];
  statuses: string[];
  isCore: boolean | null;
}

/** Default subject list filters. */
export const DEFAULT_SUBJECT_FILTERS: SubjectFilters = {
  cycles: [],
  statuses: [],
  isCore: null,
};

/** Filter group IDs for the subjects {@link DataTable} configure panel. */
export type FilterGroupId = 'cycles' | 'statuses' | 'core';

/** Configure-panel order for subject filter groups. */
export const ALL_FILTER_GROUP_IDS: FilterGroupId[] = ['cycles', 'statuses', 'core'];

/** Human-readable label per filter group (configure panel). */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  cycles: 'Cycles éducatifs',
  statuses: 'Statuts',
  core: 'Type',
};

/** Returns the badge color name for a subject status. */
export function getSubjectStatusColor(status: string): 'success' | 'error' {
  return status === 'active' ? 'success' : 'error';
}

/** {@link Select} options for subject cycle, including "none". */
export const SUBJECT_CYCLE_FORM_OPTIONS = [
  { value: '', label: 'Aucun' },
  ...Object.entries(SUBJECT_CYCLE_CONFIG).map(([value, item]) => ({
    value,
    label: item.label,
  })),
];

/** React Query keys invalidated after a subject create / save. */
export const SUBJECT_FORM_QUERY_KEYS = {
  list: ['subjects'] as const,
  one: (id: string) => ['subject', id] as const,
};
